import Cookies from 'js-cookie';

import { TokenRepository as ITokenRepository } from '../../domain/repositories/TokenRepository';

/**
 * Infrastructure implementation of TokenRepository.
 *
 * JWT is stored in `sessionStorage` so each browser tab keeps an isolated session
 * (RWT-7981). Shared cookie/`localStorage` tokens let Support overwrite one client's
 * JWT while another tab still shows the previous username/db.
 *
 * Session storage also holds large ERP-embed JWTs that exceed the ~4KB cookie limit
 * (TROC-316 / RWT-7864 WebView) — cookies are no longer the primary store.
 *
 * One-time migration: if a legacy cookie `token` or older embed key
 * `raptor.embed.token` exists and sessionStorage `token` is empty, copy into
 * sessionStorage and clear the legacy source so existing sessions survive rollout.
 */
export class TokenRepository implements ITokenRepository {
  private readonly TOKEN_KEY = 'token';
  /** Legacy key from cookie+embed dual-store (TROC-316); migrated into TOKEN_KEY. */
  private readonly LEGACY_EMBED_SESSION_KEY = 'raptor.embed.token';

  /**
   * Store the token in sessionStorage (tab-scoped) and clear any legacy stores.
   */
  setToken(token: string): void {
    try {
      sessionStorage.setItem(this.TOKEN_KEY, token);
    } catch {
      // sessionStorage unavailable (private mode quota, etc.) — still clear legacy stores
    }
    this.clearLegacyStores();
  }

  /**
   * Get the token from sessionStorage, migrating from legacy embed key or cookie when needed.
   */
  getToken(): string | undefined {
    try {
      const fromSession = sessionStorage.getItem(this.TOKEN_KEY);
      if (fromSession != null && fromSession !== '') {
        return fromSession;
      }
    } catch {
      // sessionStorage unavailable
    }

    const fromLegacyEmbed = this.readLegacyEmbedSession();
    if (fromLegacyEmbed != null) {
      this.persistMigratedToken(fromLegacyEmbed);
      return fromLegacyEmbed;
    }

    const fromCookie = Cookies.get(this.TOKEN_KEY);
    if (fromCookie == null || fromCookie === '') {
      return undefined;
    }

    this.persistMigratedToken(fromCookie);
    return fromCookie;
  }

  /**
   * Remove the token from sessionStorage and any legacy stores.
   */
  removeToken(): void {
    try {
      sessionStorage.removeItem(this.TOKEN_KEY);
    } catch {
      // sessionStorage unavailable
    }
    this.clearLegacyStores();
  }

  private readLegacyEmbedSession(): string | undefined {
    try {
      const value = sessionStorage.getItem(this.LEGACY_EMBED_SESSION_KEY);
      if (value != null && value !== '') {
        return value;
      }
    } catch {
      // ignore
    }
    return undefined;
  }

  private persistMigratedToken(token: string): void {
    try {
      sessionStorage.setItem(this.TOKEN_KEY, token);
    } catch {
      // Keep serving the migrated value this call even if write fails
    }
    this.clearLegacyStores();
  }

  private clearLegacyStores(): void {
    Cookies.remove(this.TOKEN_KEY);
    try {
      sessionStorage.removeItem(this.LEGACY_EMBED_SESSION_KEY);
    } catch {
      // ignore
    }
  }
}
